import type { JSX } from 'react';
import { useRecipientInformationGql } from '../helpers/useRecipientInformationGql';
import { SheetWrapper } from './Sheet.Wrapper';
import type { NewsletterSheetCommonProps } from './types';

/**
 * Component for rendering the Newsletter Sheet with Optimizely integration.
 *
 * @param {Object} props - The properties object.
 * @param {IntlShape} props.intl - The internationalization object for formatting messages.
 * @param {NlSheetType | false} [props.nlTexts] - The newsletter sheet texts, if available.
 * @param {boolean} props.loggedIn - Indicates if the user is logged in.
 * @param {boolean} props.useDOI - Indicates if double opt-in is used.
 * @param {Notification} props.notification - The notification object containing visibility and message.
 * @param {React.Dispatch<React.SetStateAction<Notification>>} props.setNotification - Function to set the notification state.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setForceHidden - Function to set the force hidden state.
 * @returns {JSX.Element | null} The rendered component or null if conditions to hide are met.
 */
export const NewsletterSheetOptimizely = ({
  nlTexts,
  loggedIn,
  useDOI,
  isNotificationOpen,
  notification,
  setNotification,
  setForceHidden,
}: NewsletterSheetCommonProps): JSX.Element | null => {
  const optimizelyData = useRecipientInformationGql();

  return (
    <SheetWrapper
      nlTexts={nlTexts}
      loggedIn={loggedIn}
      useDOI={useDOI}
      setNotification={setNotification}
      notification={notification}
      setForceHidden={setForceHidden}
      isNotificationOpen={isNotificationOpen}
      hasData={optimizelyData !== null}
      hasPermission={optimizelyData?.hasPermission ?? false}
      isPending={optimizelyData?.pending ?? false}
      email={optimizelyData?.email}
    />
  );
};
